/**
 * src/app/(public)/[locale]/_components/not-found-content.tsx
 *
 * 404 sayfasinin govdesi — Client Component.
 *
 * 🔴 NEDEN AYRI DOSYA: eskiden bu icerik not-found.tsx icinde sunucuda render
 * ediliyordu ve locale'i `cookies()` ile okuyordu. Next.js `not-found.tsx`'e
 * route params gecirmedigi icin cozum bu secilmisti, AMA `cookies()` dinamik
 * bir API: statik uretilen bir rotada notFound() tetiklendiginde Next.js
 * "Page changed from static to dynamic at runtime" hatasi firlatiyor ve
 * ziyaretciye **404 yerine 500** donuyordu.
 *
 * Olculdu (2026-08-13): /tr/blog/<olmayan-slug> ve /tr/yazar/<olmayan-slug>
 * 500 veriyordu; Google Search Console bunlari "Bulunamadi (404) - Dogrulama
 * basarisiz" olarak raporluyordu.
 *
 * Cozum: locale'i cerezden degil **URL yolundan** oku. `usePathname` bir
 * client hook'u ama SSR sirasinda da dogru degeri dondurur — hydration
 * uyusmazligi yok, dinamik API yok, sayfa statik kalabilir.
 * Ustelik URL cerezden daha guvenilir bir kaynak: /en/... adresine gelen
 * ziyaretci cerezi TR olsa bile Ingilizce 404 gorur.
 */
'use client'

import { usePathname } from 'next/navigation'

import { Link } from '@/lib/i18n/navigation'
import {
  ArrowRight,
  BookOpen,
  Compass,
  Home,
  Mail,
  ShieldAlert,
  Terminal,
} from 'lucide-react'
import { DEFAULT_LOCALE, SUPPORTED_LOCALES } from '@/lib/i18n/config'

import enMessages from '../../../../../messages/en.json'
import trMessages from '../../../../../messages/tr.json'

/** Dil -> mesaj paketi. Yeni dil eklenirse buraya import edilip eklenir. */
const messagesMap: Record<string, typeof trMessages> = {
  tr: trMessages,
  en: enMessages,
}

/** Aktif dilin 404 metinleri; dil taninmazsa TR'ye duser. */
function getMessages(locale: string) {
  const all = messagesMap[locale] ?? messagesMap[DEFAULT_LOCALE] ?? trMessages
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (all as any).errors?.not_found ?? trMessages.errors.not_found
}

export function NotFoundContent() {
  // Locale URL yolundan okunur: '/en/...' -> 'en'. `usePathname` SSR sirasinda
  // da dogru degeri verir, bu yuzden hydration uyusmazligi olmaz.
  const pathname = usePathname()
  const ilkSegment = pathname.split('/')[1] ?? ''
  const locale = (SUPPORTED_LOCALES as readonly string[]).includes(ilkSegment)
    ? ilkSegment
    : DEFAULT_LOCALE
  const t = getMessages(locale)

  const quickLinks = [
    { href: '/', icon: Home, title: t.links.home_title, desc: t.links.home_desc },
    {
      href: '/hizmetler',
      icon: Compass,
      title: t.links.services_title,
      desc: t.links.services_desc,
    },
    {
      href: '/blog',
      icon: BookOpen,
      title: t.links.blog_title,
      desc: t.links.blog_desc,
    },
    {
      href: '/iletisim',
      icon: Mail,
      title: t.links.contact_title,
      desc: t.links.contact_desc,
    },
  ]

  return (
    <div className="relative min-h-[calc(100vh-160px)] overflow-hidden">
      {/* ── Arka plan: layered gradient + grid pattern + blur orbs ─────────── */}
      <div
        aria-hidden
        className="absolute inset-0 -z-20 bg-gradient-to-b from-slate-50 via-white to-slate-100/50 dark:from-slate-950 dark:via-[#0a0f1f] dark:to-slate-950"
      />
      {/* Grid pattern */}
      <div
        aria-hidden
        className="absolute inset-0 -z-10 opacity-[0.06] dark:opacity-[0.10] text-slate-900 dark:text-slate-100"
        style={{
          backgroundImage:
            'linear-gradient(to right, currentColor 1px, transparent 1px), linear-gradient(to bottom, currentColor 1px, transparent 1px)',
          backgroundSize: '56px 56px',
          maskImage:
            'radial-gradient(ellipse at center, black 0%, transparent 70%)',
          WebkitMaskImage:
            'radial-gradient(ellipse at center, black 0%, transparent 70%)',
        }}
      />
      {/* Blur orb'lar */}
      <div
        aria-hidden
        className="absolute top-20 -left-20 w-[480px] h-[480px] -z-10 rounded-full bg-blue-500/20 dark:bg-blue-600/15 blur-[140px]"
      />
      <div
        aria-hidden
        className="absolute bottom-10 -right-20 w-[480px] h-[480px] -z-10 rounded-full bg-indigo-500/15 dark:bg-indigo-700/15 blur-[140px]"
      />

      <div className="container mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
        {/* ── HERO BLOĞU ─────────────────────────────────────────────────── */}
        <div className="relative">
          {/* Köşe HUD brackets — terminal estetiği */}
          <div
            aria-hidden
            className="absolute -top-4 -left-4 w-8 h-8 border-t-2 border-l-2 border-blue-500/40 dark:border-blue-400/40"
          />
          <div
            aria-hidden
            className="absolute -top-4 -right-4 w-8 h-8 border-t-2 border-r-2 border-blue-500/40 dark:border-blue-400/40"
          />
          <div
            aria-hidden
            className="absolute -bottom-4 -left-4 w-8 h-8 border-b-2 border-l-2 border-blue-500/40 dark:border-blue-400/40"
          />
          <div
            aria-hidden
            className="absolute -bottom-4 -right-4 w-8 h-8 border-b-2 border-r-2 border-blue-500/40 dark:border-blue-400/40"
          />

          <div className="relative grid lg:grid-cols-[1fr_1.2fr] gap-8 lg:gap-12 items-center py-8 lg:py-12">
            {/* Sol: stilize 404 + shield */}
            <div className="text-center lg:text-left">
              {/* Label rozeti */}
              <div className="inline-flex items-center gap-2 px-3 py-1 mb-4 rounded-full bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-900 text-blue-700 dark:text-blue-300 text-xs font-mono font-semibold">
                <ShieldAlert size={12} aria-hidden />
                {t.label}
              </div>

              {/* Stilize 404 — outline + fill kombinasyonu */}
              <div className="relative inline-block">
                <h1
                  className="text-[8rem] sm:text-[10rem] lg:text-[12rem] font-black tracking-tighter leading-none bg-clip-text text-transparent bg-gradient-to-br from-blue-600 via-indigo-600 to-blue-800 dark:from-blue-400 dark:via-indigo-400 dark:to-blue-600 select-none"
                  aria-label={`${t.code} — ${t.title}`}
                >
                  {t.code}
                </h1>
                {/* Behind layer — translate offset, blur, lower opacity (depth) */}
                <div
                  aria-hidden
                  className="absolute inset-0 text-[8rem] sm:text-[10rem] lg:text-[12rem] font-black tracking-tighter leading-none text-blue-500/10 dark:text-blue-400/10 blur-md select-none translate-x-2 translate-y-2 -z-10"
                >
                  {t.code}
                </div>
              </div>
            </div>

            {/* Sağ: terminal komut + title + subtitle */}
            <div className="space-y-5">
              {/* Terminal mock satırı */}
              <div className="font-mono text-xs sm:text-sm bg-slate-900/95 dark:bg-slate-950 border border-slate-800 dark:border-slate-700 rounded-lg p-4 shadow-2xl">
                <div className="flex items-center gap-2 mb-3 pb-3 border-b border-slate-800 dark:border-slate-700">
                  <div className="flex gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-rose-500/80" />
                    <span className="w-2.5 h-2.5 rounded-full bg-amber-500/80" />
                    <span className="w-2.5 h-2.5 rounded-full bg-emerald-500/80" />
                  </div>
                  <div className="flex items-center gap-1.5 text-slate-500 text-[10px] uppercase tracking-wider ml-2">
                    <Terminal size={10} aria-hidden />
                    securesys-shell
                  </div>
                </div>
                <div className="space-y-1.5">
                  <div className="text-emerald-400">{t.terminal_prompt}</div>
                  <div className="text-rose-400">{t.terminal_response}</div>
                  <div className="text-slate-500">
                    <span className="text-blue-400">→</span> exit code: 404
                    <span className="inline-block w-2 h-4 bg-emerald-400 ml-2 animate-pulse align-middle" />
                  </div>
                </div>
              </div>

              <div>
                <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 dark:text-slate-100 mb-3">
                  {t.title}
                </h2>
                <p className="text-base text-slate-600 dark:text-slate-400 leading-relaxed">
                  {t.subtitle}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* ── POPÜLER SAYFALAR GRID ──────────────────────────────────────── */}
        <div className="mt-16">
          <div className="flex items-center gap-3 mb-6">
            <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">
              {t.popular_pages}
            </h3>
            <div className="flex-1 h-px bg-gradient-to-r from-slate-300 dark:from-slate-700 to-transparent" />
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {quickLinks.map((link) => {
              const Icon = link.icon
              return (
                <Link
                  key={link.href}
                  href={link.href}
                  className="group relative rounded-2xl border border-slate-200 dark:border-slate-800 bg-white/80 dark:bg-slate-900/60 backdrop-blur-sm p-5 hover:border-blue-400 dark:hover:border-blue-500 hover:shadow-xl hover:shadow-blue-500/10 hover:-translate-y-0.5 transition-all duration-200"
                >
                  <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 mb-4 group-hover:bg-blue-600 group-hover:text-white dark:group-hover:bg-blue-500 transition-colors">
                    <Icon size={20} aria-hidden />
                  </div>
                  <h4 className="text-sm font-bold text-slate-900 dark:text-slate-100 mb-1">
                    {link.title}
                  </h4>
                  <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed mb-3">
                    {link.desc}
                  </p>
                  <div className="inline-flex items-center gap-1 text-xs font-semibold text-blue-600 dark:text-blue-400">
                    <span>→</span>
                    <span className="opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0 transition-all">
                      {link.title}
                    </span>
                  </div>
                </Link>
              )
            })}
          </div>
        </div>

        {/* ── FOOTER HINT ────────────────────────────────────────────────── */}
        <div className="mt-12 flex items-center justify-center">
          <div className="inline-flex items-center gap-3 px-5 py-3 rounded-full border border-slate-200 dark:border-slate-800 bg-white/50 dark:bg-slate-900/50 backdrop-blur-sm">
            <span className="text-sm text-slate-600 dark:text-slate-400">
              {t.search_hint}
            </span>
            <Link
              href="/iletisim"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
            >
              {t.contact_us}
              <ArrowRight size={14} aria-hidden />
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}
